import { inject } from '@angular/core';
import { RxStomp } from '@stomp/rx-stomp';
import { IMessage } from '@stomp/stompjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { StompConfig } from '@env/environment.type';
import { SnapshotControllerService } from '@generated/data-streamer-api/api/snapshot-controller.service';
import { SnapshotControllerService as RealtimeSnapshotControllerService } from '@generated/realtime-data-streamer-api/api/snapshot-controller.service';
import { Snapshot } from '@models/snapshot';
import { catchError } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { Increment } from '@models/increment';
import { UpdateEvent } from '@models/update-event';
import { filter, map, Observable, Subject, takeUntil, tap, throwError } from 'rxjs';
import { ResetEvent } from '@daisi/models/reset-event';
import { Event } from '@daisi/models/event';
import { LoggingService } from '@daisi/services/logging/logging.service';

export abstract class DataStreamerService<T> {
  private readonly oAuthService = inject(OAuthService);
  private readonly log = inject(LoggingService);

  private client?: RxStomp;
  private readonly unsubscribedDataSourceIds$ = new Subject<T | null>();
  protected abstract readonly snapshotControllerService:
    | SnapshotControllerService
    | RealtimeSnapshotControllerService;

  // This map keeps track of the last timestamp received for each data source.
  // Whenever the connection drops, we restart listening from the last received timestamp.
  private offsetsByDataSourceId = new Map<T, number>();

  public abstract getConfig(): StompConfig | null;

  protected connect(): void {
    const config = this.getConfig();
    // Do not attempt to connect if STOMP is not configured (e.g., in test environments)
    if (!config?.url) {
      return;
    }

    this.client = new RxStomp();

    this.client.configure({
      brokerURL: config.url,
      heartbeatIncoming: config.heartBeat,
      heartbeatOutgoing: config.heartBeat,
      beforeConnect: (client) => {
        client.configure({
          connectHeaders: {
            Authorization: `Bearer ${this.oAuthService.getAccessToken()}`,
          },
        });
      },
    });

    this.client?.stompErrors$.subscribe((error) => {
      console.error('An error occurred in the StompClient: ' + error.headers['message']);
    });

    this.client.activate();
  }

  disconnect(): void {
    this.client?.deactivate();
  }

  listen(topic: T, getTimestampOffset: () => number): Observable<IMessage> {
    if (!this.client) {
      this.connect();
    }

    this.unsubscribedDataSourceIds$.next(null);
    return this.client!.watch({
      destination: topic + '',
      subHeaders: () => {
        const offsetInSecondsPrecision = Math.floor(
          new Date(getTimestampOffset()).getTime() / 1000,
        );
        return {
          'prefetch-count': '1',
          'x-stream-offset': `timestamp=${offsetInSecondsPrecision}`,
          ack: 'client-individual',
        };
      },
    }).pipe(takeUntil(this.unsubscribedDataSourceIds$.pipe(filter((tpc) => tpc === topic))));
  }

  stopListening(topic: T): void {
    this.unsubscribedDataSourceIds$.next(topic);
  }

  fetchSnapshot(dataSourceId: T): Observable<Snapshot> {
    return this.snapshotControllerService.getSnapshotById(dataSourceId + '').pipe(
      map((response) => {
        return {
          timestamp: response.timestamp,
          data: response.data,
          basemapVersion: response.basemapVersion,
        } as Snapshot;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => new SnapshotError(dataSourceId, err)),
      ),
    );
  }

  onIncrement(id: T, timestampOffset: number): Observable<Increment> {
    this.offsetsByDataSourceId.set(id, timestampOffset);
    return this.listen(id, () => this.offsetsByDataSourceId.get(id) ?? 0).pipe(
      tap(() => {
        if (!this.oAuthService.hasValidAccessToken()) {
          this.log.warn('Unauthorized access token - logging out');
          this.oAuthService.logOut();
        }
      }),
      map((message) => this.parseEvent(message)),
      filter((event): event is Event => event !== null),
      tap((event) => this.offsetsByDataSourceId.set(id, event.timestamp)),
      filter((event) => event.timestamp > timestampOffset),
      filter(
        (event) => event.type === 'StreamingResetEvent' || event.type === 'StreamingUpdateEvent',
      ),
      map((event) => ({ id, event: event as UpdateEvent | ResetEvent }) as Increment),
    );
  }

  /**
   * Reads one stream message, or null when its body is not valid JSON. Acks either way: with
   * `prefetch-count: 1` an unacked message stalls the whole subscription, so a single unreadable
   * message would otherwise silently freeze the live data.
   */
  private parseEvent(message: IMessage): Event | null {
    try {
      return JSON.parse(message.body) as Event;
    } catch (error) {
      this.log.error('Unparseable message on the stream, skipping it', error);
      return null;
    } finally {
      message.ack();
    }
  }
}

export class SnapshotError<K> extends Error {
  constructor(
    readonly id: K,
    cause: HttpErrorResponse,
  ) {
    super(`Error loading snapshot '${id}': ${cause.message}`);
    this.cause = cause;
  }
}
